import { ExporttocsvService, csvDate, csvCurrency } from '../../../services/exporttocsv/exporttocsv.service';
import { Component, Input, OnChanges, OnDestroy, OnInit, Pipe, PipeTransform, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Purchase, TransactionsService } from '../../../services/transactions/transactions.service';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TeamsService } from '../../../services/teams/teams.service';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { StripeService } from '../../../services/stripe/stripe.service';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { SnackBarComponent } from '../../snackbar/snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ImageDialogComponent, ImageDialogModel } from '../../modal/imagedialog/imagedialog.component';
import { UsersService } from '../../../services/users/users.service';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { FeeService } from 'src/app/services/fee/fee.service';
import { EditPurchaseDialogData, EditPurchasePageComponent } from '../../purchases/edit-purchase/edit-purchase.page';
import { PurchaseInterface } from '../../purchases/purchase.interface';
import { Subscription } from 'rxjs';

interface ReimbursementDetails {
    reimbursementSource?: 'receipt' | 'bankTransaction';
    teamAmountReimbursed?: number | string;
}

@Component({
    selector: 'app-analytics-approve',
    templateUrl: './analytics-approve.component.html',
    styleUrls: ['./analytics-approve.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AnalyticsApproveComponent implements OnChanges, OnInit, OnDestroy {

  exportCsv(title: string): void {
    this.csvExport.exportTable(title, this.items, {
      "image": {header: "Receipt", value: () => ''},
      "reimbursementSource": {header: "Source", value: (item: any) => item.reimbursementSource === 'receipt' ? 'Uploaded receipt' : 'Linked-bank transaction'},
      "purchaseDate": {header: "Purchase Date", value: (item: any) => csvDate((item.purchaseDate ?? item.transactionDate), 'mediumDate', 'UTC')},
      "reimbursedToWallit": {header: "Reimbursed To Wallit", value: (item: any) => (csvDate(item.answeredAt, 'mediumDate', 'UTC')) || '---'},
      "user": {header: "Member", value: (item: any) => (!item.firstName && !item.lastName) ? '---' : (item.firstName + ' ' + item.lastName)},
      "employeeNumber": {header: "Employee ID", value: (item: any) => item.employeeNumber ?? '---'},
      "merchant": {header: "Merchant", value: (item: any) => item.merchant ?? item.name},
      "purpose": {header: "Purpose", value: (item: any) => item.purpose ?? '---'},
      "project": {header: "Project", value: (item: any) => item.project || '---'},
      "category": {header: "Category", value: (item: any) => item.category || item.categories || '---'},
      "amount": {header: "Amount", value: (item: any) => csvCurrency(item.amount)},
      "billable": {header: "Billable", value: (item: any) => item.billable ? 'Yes' : 'No'},
      "taxable": {header: "Taxable", value: (item: any) => item.taxable ? 'Yes' : 'No'},
      "state": {header: "Status", value: (item: any) => this.getState(item)},
    }, this.columnsToDisplay, {exclude: ['action', 'actionmenu']});
  }

  constructor(
        private transactionsService: TransactionsService,
        private teamsService: TeamsService,
        private route: ActivatedRoute,
        private formBuilder: UntypedFormBuilder,
        private snackBar: MatSnackBar,
        private stripeService: StripeService,
        private uiAlertService: UiAlertService,
        private usersService: UsersService,
        private dialog: MatDialog,
        private spinnerService: SpinnerService,
        private feeService: FeeService,
      private csvExport: ExporttocsvService
  ) {
        this.form = this.formBuilder.group({
            member: new UntypedFormControl(0),
            status: new UntypedFormControl(0)
        }
        );
        this.form.valueChanges.subscribe(() => {
            const memberIndex = this.form.get('member')?.value;
            this.userId = memberIndex ? this.members?.[memberIndex]?.userId : 'all';
            if (!this.teamId) {
                this.items.data = [];
                return;
            }
            this.loadData().then(results => {
                this.items.data = results;
            });
        });
    }

    @Input() activeTitle: string | undefined;
    @Input() team: any;

    public columnsToDisplay = [
        'image', 'purchaseDate', 'merchant', 'purpose', 'project', 'user', 'employeeNumber', 'category', 'amount', 'billable', 'taxable', 'state', 'reimbursementSource', 'action'
    ];

    SCROLL_CHUNK = 1000;

    @Input() analytics: any;
    @Input() filter = '';
    // @ts-ignore
    @ViewChild(MatSort) sort: MatSort;

    public statuses = this.transactionsService.purchaseStatuses;
    public members: any;

    public items = new MatTableDataSource<Purchase>();
    private page = 0;
    private teamId = '';
    private searchValue = '';

    public userId = 'all';
    public form: FormGroup;
    public fileName = '';

    serviceFee = 0;
    private wellspaceSubscription?: Subscription;
    private routeSubscription?: Subscription;

    ngOnChanges(changes: SimpleChanges) {
        if (changes.activeTitle) {
            this.updateColumnsToDisplay();
        }

        if (!changes.team || changes.team.firstChange) {
            return;
        }

        if (this.team) {
            this.unsubscribeFromWellspace();
            void this.setTeamContext(this.team);
            return;
        }

        this.subscribeToWellspace();
    }

    ngOnInit(): void {
        this.routeSubscription = this.route.params.subscribe(params => {
            if (params.userId) {
                this.userId = params.userId;
            }

            if (this.team) {
                this.unsubscribeFromWellspace();
                void this.setTeamContext(this.team);
                return;
            }

            this.subscribeToWellspace();
        });
    }

    ngOnDestroy(): void {
        this.routeSubscription?.unsubscribe();
        this.unsubscribeFromWellspace();
    }

    private async loadData(): Promise<any> {
        if (!this.teamId) {
            return [];
        }

        let purchases = await this.transactionsService.getPurchases(this.teamId, this.userId, this.filter === 'Reimbursement approved' ? 'Reimbursed' : this.filter);
        if (this.filter === 'Reimbursement approved' || this.filter === 'Reimbursed') {
            const partialReimbursements = await this.transactionsService.getPurchases(this.teamId, this.userId, 'Partial Reimbursed');
            purchases.push(...partialReimbursements);
        }
        purchases = purchases.map((purchase: any) => {
            const thisMember = this.members.find(
                (member: any) => member.userId === purchase.userId
            );
            purchase.reimbursementSource = 'receipt';
            purchase.employeeNumber = thisMember?.employeeNumber;
            purchase.firstName = thisMember?.firstName;
            purchase.lastName = thisMember?.lastName;
            return purchase;
        });
        purchases.sort((a: any, b: any) => {
            return a.createdAt > b.createdAt ? -1 : 1;
        });
        let transactions = await this.transactionsService.listTransactions(this.teamId, this.userId, this.page, this.SCROLL_CHUNK, this.searchValue, 2);
        transactions = transactions.map((transaction: any) => {
            transaction.reimbursementSource = 'bankTransaction';
            transaction.employeeNumber = this.members.find((member: any) => member.userId === transaction.userId)?.employeeNumber;
            return transaction;
        });
        return this.filter === 'Reimbursed' ? [...purchases, ...transactions] : purchases;
    }

    private async setTeamContext(team: any): Promise<void> {
        this.team = team;
        this.teamId = team?.id || '';

        if (!this.teamId) {
            this.members = [{ firstName: 'All', lastName: 'members' }];
            this.items.data = [];
            return;
        }

        const teamMembers = await this.teamsService.getTeamMembers(this.teamId);
        const members = Array.isArray(teamMembers) ? teamMembers : [];
        this.members = [{ firstName: 'All', lastName: 'members' }, ...members];
        const index = members.findIndex((thisMember: any) => thisMember.userId === this.userId);
        const selectedIndex = index < 0 ? 0 : index + 1;
        this.form.get('member')?.setValue(selectedIndex, { emitEvent: false });
        const member = this.members[selectedIndex] || this.members[0];
        this.fileName = `${team?.name || 'Team'} reimbursements ${member.firstName} ${member.lastName}`;
        this.items.data = await this.loadData();
        this.items.sort = this.sort;
    }

    private subscribeToWellspace(): void {
        if (this.team || this.wellspaceSubscription) {
            return;
        }

        this.wellspaceSubscription = this.teamsService.wellspaceChangedObservable.subscribe((wellspace: any) => {
            if (wellspace) {
                void this.setTeamContext(wellspace);
            }
        });
    }

    private unsubscribeFromWellspace(): void {
        this.wellspaceSubscription?.unsubscribe();
        this.wellspaceSubscription = undefined;
    }

    filterItems(event: any): void {
        this.searchValue = event.target.value;
        this.loadData().then(results => {
            this.items.data = results;
        });
    }

    async reimbursePurchase(item: Purchase & ReimbursementDetails): Promise<void> {
        if (!this.canManageReceipt(item)) {
            return;
        }
        if (this.team.defaultPaymentMethod === '') {
            this.snackBar.openFromComponent(SnackBarComponent, { data: 'A payment method is needed for receipt approval' });
            return;
        }

        try {
            this.serviceFee = await this.feeService.getPaymentMethodFee(this.team);
        } catch (error: any) {
            this.snackBar.openFromComponent(SnackBarComponent, {
                data: error?.message || 'Unable to use the default funding method. Please choose a valid funding method in Billing.',
            });
            return;
        }
        const amount: number = parseFloat(item.amount);
        const amountPlusFee: number = amount * (1 + this.serviceFee);

        this.uiAlertService.presentAlertConfirm(`This will initiate a one time charge to your default payment method of $${amountPlusFee.toFixed(2)}. ${this.serviceFee > 0 ? `This includes a service fee of $${(amountPlusFee - amount).toFixed(2)}.` : ''} Any available credit balance will be applied first before charging the remaining amount.`).then(async (confirm: any) => {
            if (confirm) {
                this.uiAlertService.presentFieldInput('Reimbursement Approval Details', 'Info', '').then(async result => {
                    if (result) {
                        this.spinnerService.show();
                        const member = this.members.find((thisMember: {
                            userId: string;
                        }) => thisMember.userId === item.userId);
                        const customerId = await this.teamsService.getCustomerId(this.team);
                        this.stripeService.makeOneTimePayment(customerId, '_RECEIPT_', amount, [member?.userEmail], item.info, {
                            receiptUrl: item.receiptUrl,
                            purpose: item.purpose,
                            merchant: item.merchant,
                            purchaseDate: item.purchaseDate
                        }, await this.teamsService.getPaymentMethod(this.team), this.team.id, 'REIMBURSEMENT', this.team.id, true).then(() => {
                            this.transactionsService.setPurchaseState(this.teamId, item.userId, item.id, 'Reimbursed', result).then(() => {
                                this.loadData().then(results => {
                                    this.items.data = results;
                                });
                                this.spinnerService.hide();
                                this.snackBar.openFromComponent(SnackBarComponent, { data: 'Purchase has been reimbursed to the member\'s Wallit balance' });
                            }).catch(err => {
                                this.spinnerService.hide();
                                this.snackBar.openFromComponent(SnackBarComponent, { data: err.error && err.error.message ? err.error.message : 'An error has occurred.' });
                                console.error(err);
                            });
                        }).catch(err => {
                            this.spinnerService.hide();
                            this.snackBar.openFromComponent(SnackBarComponent, { data: err.error && err.error.message ? err.error.message : 'An error has occurred.' });
                            console.error(err);
                        });
                    }
                });
            }
        });
    }

    async reimbursePurchaseWithAllowance(item: Purchase & ReimbursementDetails): Promise<void> {
        if (!this.canManageReceipt(item)) {
            return;
        }
        const member = this.findMemberForPurchase(item);
        const amount = this.parseAmount(item.amount);
        const balance = this.parseAmount(member?.balance);

        if (!member) {
            this.snackBar.openFromComponent(SnackBarComponent, { data: 'Unable to find the member for this receipt' });
            return;
        }

        if (amount <= 0) {
            this.snackBar.openFromComponent(SnackBarComponent, { data: 'Unable to determine the receipt amount' });
            return;
        }

        if (balance <= 0) {
            this.snackBar.openFromComponent(SnackBarComponent, { data: 'This member does not have an allowance balance to use' });
            return;
        }

        const isPartial = balance < amount;
        const confirmMessage = isPartial
            ? `This member's Allowance Balance is not enough for a full reimbursement.<br>Do you want to partially reimburse $${balance.toFixed(2)} from their Allowance Balance?`
            : `This will reimburse $${amount.toFixed(2)} from this member's Allowance Balance to their Wallit balance.`;
        const confirm = await this.uiAlertService.presentAlertConfirm(confirmMessage);

        if (!confirm) {
            return;
        }

        const result = await this.uiAlertService.presentFieldInput('Reimbursement Approval Details', 'Info', '');

        if (!result) {
            return;
        }

        await this.completeAllowanceReimbursement(item, result, isPartial);
    }

    denyPurchase(item: Purchase & ReimbursementDetails): void {
        if (!this.canManageReceipt(item)) {
            return;
        }
        this.uiAlertService.presentFieldInput('Reimbursement Denial Details', 'Info', '').then(result => {
            if (result) {
                this.transactionsService.setPurchaseState(this.teamId, item.userId, item.id, 'Reimbursement denied', result).then(() => {
                    this.loadData().then(results => {
                        this.items.data = results;
                    });
                    this.snackBar.openFromComponent(SnackBarComponent, { data: 'Purchase reimbursement has been denied' });
                });
            }
        });
    }

    async makePayments(): Promise<void> {
        const payments = this.items.data.map(purchase => ({
            user: purchase.userId,
            amount: purchase.amount,
            date: purchase.purchaseDate,
            merchant: purchase.merchant,
            info: purchase.info
        }));
        let amount = 0;
        payments.forEach(payment => {
            amount += parseFloat(payment.amount);
        });

        const fee = await this.feeService.getFee(this.teamId);
        this.serviceFee = fee.ach.fee;

        const amountPlusFee = amount * (1 + this.serviceFee);
        const paymentMethodDescription = ` to your default payment method`;
        this.uiAlertService.presentAlertConfirm(`This will initiate a one time ${this.team.invocieEmail ? 'invoice' : 'charge'} of $${amountPlusFee.toFixed(2)} for ${payments.length} purchase${payments.length === 1 ? '' : 's'}${paymentMethodDescription}. ${this.serviceFee > 0 ? `This includes a service fee of $${(amountPlusFee - amount).toFixed(2)}.` : ''} Any available credit balance will be applied first before charging the remaining amount.`).then(async (confirm: any) => {
            if (confirm) {
                await this.stripeService.makePurchasePayments(this.team.id, payments).then(() => {
                }).then(() => {
                    this.items.data.forEach(purchase => purchase.state = 'Reimbursed');
                    this.items.data = [];
                    this.snackBar.openFromComponent(SnackBarComponent, { data: `Purchase reimbursements made.` });
                }).catch((error: any) => {
                    const info = error.error;
                    this.snackBar.openFromComponent(SnackBarComponent, { data: `${info.raw.message}: ${info.raw.decline_code.split('_').join(' ')}` });
                });
            }
        });
    }

    getState(item: Purchase & ReimbursementDetails): string {
        if (item.reimbursementSource === 'bankTransaction') {
            // Bank rows come from the reimbursed-only endpoint (teamPaid=1 and amount reimbursed > 0).
            return Number(item.teamAmountReimbursed) < Number(item.amount) ? 'Partial Reimbursed' : 'Reimbursed';
        }
        return item.state ? item.state : 'Awaiting Approval';
    }

    canManageReceipt(item: ReimbursementDetails & { state?: string }): boolean {
        return item.reimbursementSource === 'receipt' && item.state !== 'Reimbursed';
    }

    showReceipt(item: Purchase): void {
        this.dialog.open(ImageDialogComponent, { data: new ImageDialogModel('Receipt', item.receiptUrl) });
    }

    private async completeAllowanceReimbursement(item: Purchase, info: string, isPartial: boolean): Promise<void> {
        try {
            this.spinnerService.show('Please wait...');
            await this.transactionsService.reimbursePurchaseWithAllowance(this.teamId, item.userId, item.id, {
                info,
                isPartial
            });
            this.items.data = await this.loadData();
            this.snackBar.openFromComponent(SnackBarComponent, {
                data: isPartial
                    ? 'Purchase has been partially reimbursed from the member\'s allowance balance'
                    : 'Purchase has been reimbursed from the member\'s allowance balance'
            });
        } catch (error: any) {
            this.snackBar.openFromComponent(SnackBarComponent, {
                data: error.error && error.error.message
                    ? error.error.message
                    : 'An error has occurred while trying to reimburse the purchase from allowance balance.'
            });
        } finally {
            this.spinnerService.hide();
        }
    }

    private findMemberForPurchase(item: Purchase): any {
        return this.members.find((thisMember: { userId: string }) => thisMember.userId === item.userId);
    }

    private parseAmount(amount: string | number | undefined): number {
        if (amount === undefined || amount === null) {
            return 0;
        }

        const parsedAmount = typeof amount === 'number' ? amount : parseFloat(amount);
        return Number.isNaN(parsedAmount) ? 0 : parsedAmount;
    }

    private updateColumnsToDisplay(): void {
        const reimbursedToWallitIndex = this.columnsToDisplay.indexOf('reimbursedToWallit');
        if (this.activeTitle !== 'Receipts to Approve' && this.activeTitle !== 'Denied Receipts') {
            if (reimbursedToWallitIndex === -1) {
                this.columnsToDisplay.splice(2, 0, 'reimbursedToWallit');
            }
        } else {
            if (reimbursedToWallitIndex !== -1) {
                this.columnsToDisplay.splice(reimbursedToWallitIndex, 1);
            }
        }
    }

    editTransaction(item: PurchaseInterface & ReimbursementDetails): void {
        if (!this.canManageReceipt(item)) {
            return;
        }
        this.dialog.open(EditPurchasePageComponent, {
            width: '720px',
            maxWidth: '95vw',
            disableClose: true,
            autoFocus: false,
            data: { teamId: this.teamId, userId: item.userId, purchase: item } as EditPurchaseDialogData
        }).afterClosed().subscribe(() => {
            this.loadData().then(results => {
                this.items.data = results;
            });
        });
    }
}

@Pipe({
    name: 'getState',
    pure: true,
    standalone: false
})
export class GetStatePipe implements PipeTransform {

    transform(item: any, thisArg: AnalyticsApproveComponent): string {
        return thisArg.getState(item);
    }

}
